/** Shared API types, mirroring the Go handlers' JSON shapes. */

export type LifecycleStatus =
  | 'registered'
  | 'expired'
  | 'redemptionPeriod'
  | 'pendingDelete'
  | 'available'
  | 'reserved'
  | 'unknown'

export type MatchStatus =
  | 'matched'
  | 'pending_verification'
  | 'no_longer_matched'
  | 'stale'

export interface Page<T> {
  items: T[]
  next_cursor?: string
  total?: number
  total_is_estimate?: boolean
}

export interface User {
  id: string
  email: string
  display_name: string
  role: 'user' | 'admin'
  status: string
  timezone: string
  locale: string
  enforcement_stage?: string
  created_at: string
  last_login_at?: string
}

export interface Entitlement {
  key: string
  limit?: number
  enabled?: boolean
  over_policy: string
  period: string
}

export interface EntitlementSet {
  user_id: string
  plan_code: string
  plan_name: string
  plan_version: number
  subscription_status: string
  entitlements: Record<string, Entitlement>
  resolved_at: string
}

export interface UsageItem {
  usage_type: string
  entitlement: string
  label: string
  used: number
  limit: number
  unlimited: boolean
  over_policy: string
}

export interface UsageSummary {
  user_id: string
  period: string
  plan_code: string
  items: UsageItem[]
  attributed_cost_micros: number
  currency: string
}

export interface Domain {
  id: string
  domain_ascii: string
  domain_unicode: string
  registrable_domain: string
  sld: string
  tld: string
  label_length: number
  total_length: number
  charset_mask: number
  charset_class: string
  feature_bits: number
  feature_tags: string[]
  current_status: LifecycleStatus
  last_known_status?: string
  status_source: string
  status_confidence: number
  status_changed_at?: string
  expires_at?: string
  registered_at?: string
  registration_updated_at?: string
  nameservers: string[]
  drop_estimate_at?: string
  drop_estimate_source?: string
  release_estimate_start_at?: string
  release_estimate_end_at?: string
  release_estimate_samples?: number
  first_seen_at?: string
  last_seen_at?: string
  last_resolved_at?: string
  next_check_at?: string
  monitor_tier: number
  sources: string[]
  intrinsic_value_score?: number
  score_version?: string
  registration_count?: number
  drop_count?: number
  current_registrar?: string
  /** The directory's display name; absent when the domain is not linked. */
  registrar_display?: string
  /** ---- 中文语义（EPIC-23），由标签算出 ---- */
  zh_pinyin_syllables?: number
  zh_pinyin_ambiguous?: boolean
  zh_initial_abbr?: boolean
  zh_lucky?: boolean
  zh_in_dictionary?: boolean
  zh_words?: string
  zh_note?: string
  /** ---- 模型给出的释义，只有分析过的域名才有 ---- */
  zh_ai_reading?: string
  zh_ai_meaning?: string
  /** 该字符串本身是否是英文词，不是中文含义的翻译。 */
  zh_ai_english?: string
  zh_ai_note?: string
  zh_ai_at?: string
  /** ---- 语义库里人工填写的内容，优先级高于模型和规则 ---- */
  zh_manual_reading?: string
  zh_manual_meaning?: string
  zh_manual_note?: string
  /** 服务端算好的最终标签：人工接管则用人工的，否则是标签规则 + 内置规则。 */
  zh_tags?: string[]
  registrar_iana_id?: string
  registrar_abuse_email?: string
  registrar_abuse_phone?: string
  gfw_risk_status: string
  version: number
  created_at: string
  updated_at: string
  match_count?: number
  matched_rules?: string[]
  favorite: boolean
  watched: boolean
  watcher_id?: string
  watcher_state?: string
  watcher_priority?: string
}

export interface Observation {
	id: number
  source: string
  queried_at: string
  normalized_status: string
  raw_statuses?: string[]
  events?: Record<string, string>
  nameservers?: string[]
  details?: {
    registered_at?: string
    updated_at?: string
    registrar_iana_id?: string
    registrar_abuse_email?: string
    registrar_abuse_phone?: string
    additional_sources?: string[]
  }
  confidence: number
  http_status?: number
  latency_ms?: number
  registrar?: string
  expires_at?: string
  error_class?: string
  raw_sources?: string[]
}

export interface RawObservation {
  observation_id: number
  domain_ascii: string
  source: string
  queried_at: string
  content_type: 'application/json' | 'text/plain'
  filename: string
  sha256: string
  size_bytes: number
  body: string
}

export interface DomainDetail {
  domain: Domain
  observations: Observation[]
  freshness: {
    fresh: boolean
    ttl_seconds: number
    last_resolved_at?: string
    status_source: string
  }
  disclaimer: string
}

export interface DomainEvent {
  id: string
  domain_id?: string
  domain?: string
  event_type: string
  from_status?: string
  to_status?: string
  observed_at: string
  recorded_at?: string
  source: string
  confidence: number
  metadata?: Record<string, unknown>
}

export interface Rule {
  id: string
  name: string
  description: string
  rule_json: RuleDocument
  schema_version: number
  version: number
  mode: 'query' | 'continuous'
  status: 'draft' | 'active' | 'paused' | 'error' | 'archived'
  candidate_budget: number
  plan_summary?: Estimate
  match_count: number
  last_error?: string
  last_run_at?: string
  next_run_at?: string
  created_at: string
  updated_at: string
  /** Set when the watch retired because the name became available. */
  released_at?: string
}

// ---- Rule document (mirrors internal/ruleengine) ----

export interface RuleDocument {
  schema_version: number
  name: string
  mode: 'query' | 'continuous'
  /** 生成器的候选上限。缺省即服务端默认值；只对会生成候选的规则有意义。 */
  candidate_budget?: number
  root: RuleGroup
  schedule?: {
    trigger?: string[]
    cron?: string
    backtrack_days?: number
    interval_minutes?: number
  }
}

export interface RuleGroup {
  type: 'group'
  op: 'AND' | 'OR'
  not?: boolean
  disabled?: boolean
  id?: string
  children: RuleNode[]
}

export interface RuleCondition {
  type: 'condition'
  field: string
  operator: string
  value: unknown
  not?: boolean
  disabled?: boolean
  id?: string
}

export type RuleNode = RuleGroup | RuleCondition

export function isGroup(node: RuleNode): node is RuleGroup {
  return node.type === 'group'
}

export interface EnumOption {
  value: string
  label: string
  hint?: string
  hidden?: boolean
}

export interface FieldSpec {
  name: string
  /** Chinese display name; comes from the engine so the two cannot diverge. */
  label: string
  class:
    | 'generation_constraint'
    | 'local_match'
    | 'indexable'
    | 'computed_feature'
    | 'external_state'
  operators: Record<string, string>
  enum?: EnumOption[]
  pushdown_operators?: string[]
  description: string
  /** Declared in the schema but not yet evaluable (§16.1.1). */
  deferred?: boolean
  deferred_note?: string
}

export interface Estimate {
  upper_bound: number
  upper_bound_saturated: boolean
  sample_size: number
  hit_rate: number
  hit_rate_low: number
  hit_rate_high: number
  pending_rate: number
  expected_matches: number
  expected_matches_low: number
  expected_matches_high: number
  relative_error: number
  within_acceptable_error: boolean
  plan: {
    tlds: string[]
    min_length: number
    max_length: number
    alphabet_name: string
    prefix?: string
    suffix?: string
    pushed: Array<{ field: string; operator: string; class: string; path: string }>
  }
  warnings?: string[]
  reject_reason?: string
  estimated_duration_seconds: number
}

export interface RuleValidation {
  valid: boolean
  estimate?: Estimate
  stats?: {
    nodes: number
    conditions: number
    groups: number
    max_depth: number
    by_class: Record<string, number>
    has_regex: boolean
    has_external: boolean
    deferred_fields?: string[]
  }
  has_external_conditions?: boolean
  notice?: string
  errors?: Array<{ path: string; message: string }>
}

export interface EvidenceNode {
  path: string
  type: string
  field?: string
  operator?: string
  op?: string
  not?: boolean
  result: string
  reason?: string
  children?: EvidenceNode[]
}

export interface TestResult {
  input: string
  domain?: string
  outcome?: 'matched' | 'rejected' | 'pending_verification'
  undetermined?: string[]
  features?: string[]
  evidence?: EvidenceNode
  error?: string
}

export interface RuleRun {
  id: string
  rule_version: number
  trigger: string
  status: string
  candidates_seen: number
  candidates_matched: number
  candidates_rejected: number
  domains_created: number
  pending_verification: number
  error_code?: string
  error_message?: string
  started_at?: string
  finished_at?: string
  created_at: string
}

export interface RuleMatch {
  id: number
  match_status: MatchStatus
  matched_at?: string
  last_matched_at?: string
  rule_version: number
  verify_attempts: number
  next_verify_at?: string
  evidence?: Record<string, unknown>
  domain_id: string
  domain: string
  tld: string
  current_status: LifecycleStatus
  status_source: string
  last_resolved_at?: string
  value_score?: number
}

export interface Watcher {
  id: string
  domain_id: string
  domain: string
  tld: string
  status: string
  priority: 'normal' | 'high' | 'critical'
  event_filter?: Record<string, unknown>
  post_registration_action: string
  note?: string
  current_status: LifecycleStatus
  status_source: string
  last_resolved_at?: string
  next_check_at?: string
  monitor_tier: number
  created_at: string
  updated_at: string
  /** Set when the watch retired because the name became available. */
  released_at?: string
}

export interface NotificationChannel {
  id: string
  name: string
  kind: 'inapp' | 'email' | 'webhook' | 'telegram'
  config: Record<string, unknown>
  secret_masked?: string
  status: 'unverified' | 'active' | 'failed' | 'disabled'
  verified_at?: string
  last_error?: string
  created_at: string
}

export interface Delivery {
  id: string
  channel_kind: string
  event_type: string
  subject: string
  body: string
  status: string
  scheduled_at: string
  delay_seconds: number
  attempts: number
  last_error?: string
  sent_at?: string
  read_at?: string
  created_at: string
  domain_id?: string
}

export interface DashboardSummary {
  kpi: Record<string, number>
  my_matches: { matched: number; pending_verification: number }
  facets: {
    by_status: Record<string, number>
    by_tld: Record<string, number>
    by_feature: Record<string, number>
  }
  trend: Array<{ day: string; counts: Record<string, number> }>
  anomalies: Array<{
    kind: string
    severity: 'info' | 'warning' | 'critical'
    message: string
    count?: number
    admin_only?: boolean
  }>
  usage: UsageSummary
  generated_at: string
}

export interface ReleaseAnalyticsSummary {
  total_cycles: number
  demo_cycles: number
  confirmed_available: number
  reregistered_without_available: number
  renewed: number
  tracking: number
  renewal_candidates: number
  median_delay_days?: number
  p25_delay_days?: number
  p75_delay_days?: number
  p90_delay_days?: number
  average_observation_window_minutes?: number
}

export interface ReleaseAnalyticsGroup {
  tld: string
  registrar: string
  samples: number
  confirmed_samples: number
  reregistered_without_available: number
  demo_samples: number
  median_delay_days: number
  p25_delay_days: number
  p75_delay_days: number
  p90_delay_days: number
  min_delay_days: number
  max_delay_days: number
  peak_release_hour: number
  peak_hour_samples: number
  peak_hour_percentage: number
  low_confidence: boolean
}

export interface ReleaseHourBucket {
  hour: number
  count: number
  percentage: number
}

export interface ReleaseCycle {
  id: string
  domain_id: string
  domain_ascii: string
  tld: string
  registrar_at_expiry?: string
  original_registered_at?: string
  expires_at: string
  last_unavailable_at?: string
  first_available_at?: string
  re_registered_at?: string
  renewal_candidate_at?: string
  renewal_confirm_after?: string
  renewed_at?: string
  new_expires_at?: string
  outcome: 'tracking' | 'renewal_candidate' | 'renewed' | 'available_confirmed'
    | 'reregistered_without_availability' | 'available_then_registered' | 'unresolved'
  availability_source?: string
  confidence: number
  release_delay_seconds?: number
  observation_window_seconds?: number
  is_demo: boolean
}

export interface ReleaseAnalytics {
  summary: ReleaseAnalyticsSummary
  groups: ReleaseAnalyticsGroup[]
  hourly_distribution: ReleaseHourBucket[]
  cycles: ReleaseCycle[]
  filters: { tlds: string[]; registrars: string[] }
  timezone: string
  generated_at: string
  scope: 'all' | 'demo' | 'observed'
  notice: string
}

export interface TldInfo {
  tld: string
  kind: string
  zone_access: string
  zone_authorized: boolean
  rdap_supported: boolean
  enabled: boolean
  market_weight: number
  authorization_note?: string
  bulk_discovery_supported: boolean
}

export interface RegistrarOption {
  registrar: string
  name: string
  commission_note?: string
  action_url: string
}

export interface AffiliateOffer {
  domain: string
  options: RegistrarOption[]
  disclosure: string
  disclosure_version: string
  ranking_explanation: string
  status_at_offer: string
  status_confirmed_at?: string
  status_warning?: string
  guarantee: string
}

export interface QueueStat {
  queue: string
  length: number
  pending: number
  delayed: number
  oldest_pending_seconds: number
  consumers: number
  dead_letters: number
}

export interface AdminHealth {
  queues: QueueStat[]
  monitor_tiers: Array<{
    tier: number
    name: string
    domains: number
    overdue: number
    freshness_p50_seconds?: number
    freshness_p95_seconds?: number
  }>
  circuit_breakers: Record<string, string>
  status_freshness: Record<string, number | null>
  cost_reconciliation_issues: number
  backpressure: { pause_low_value_scans: boolean; reasons?: string[] }
  checked_at: string
}

export interface DeadLetter {
  job_id: string
  queue: string
  job_type: string
  attempt: number
  error_class: string
  error_message: string
  first_failed_at?: string
  last_failed_at?: string
  trace_id: string
}

export interface APIToken {
  id: string
  name: string
  prefix: string
  scopes: string[]
  last_used_at?: string
  expires_at?: string
  revoked_at?: string
  created_at: string
}
